import { useEffect, useState } from "react";

import { api, errMsg } from "../../lib/api";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Banner } from "../ui/Banner";
import { Field } from "../ui/Field";
import { QtyStepper } from "../ui/QtyStepper";
import { ProductSearch, SearchProduct } from "../ui/ProductSearch";
import { ProductThumb } from "../ui/ProductThumb";

type Line = {
  sku: string;
  name: string;
  image_url: string | null;
  category: string | null;
  stock: number;
  qty: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onDone: (summary: string) => void;
};

export function MysteryBatchModal({ open, onClose, onDone }: Props) {
  const [label, setLabel] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLabel("");
    setLines([]);
    setBusy(false);
    setError(null);
  }, [open]);

  function addProduct(p: SearchProduct) {
    setLines((prev) => {
      if (prev.some((l) => l.sku === p.sku)) return prev;
      return [
        ...prev,
        { sku: p.sku, name: p.name, image_url: p.image_url, category: p.category, stock: p.quantity, qty: 1 },
      ];
    });
  }

  function setQty(sku: string, qty: number) {
    setLines((prev) => prev.map((l) => (l.sku === sku ? { ...l, qty } : l)));
  }

  function removeLine(sku: string) {
    setLines((prev) => prev.filter((l) => l.sku !== sku));
  }

  const totalUnits = lines.reduce((s, l) => s + l.qty, 0);
  const canConfirm = label.trim().length >= 2 && lines.length >= 1 && !busy;

  async function confirm() {
    if (!canConfirm) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(
        "/api/stock-out/mystery-batch",
        {
          batch_label: label.trim(),
          lines: lines.map((l) => ({ sku: l.sku, quantity: l.qty })),
        },
        { headers: { "X-Idempotency-Key": crypto.randomUUID() } },
      );
      onDone(`Batch "${data.batch_label}" · ${data.events.length} prodotti scaricati`);
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Produzione Mystery"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" style={{ flex: 1 }} disabled={!canConfirm} onClick={confirm}>
            <Icon name="out" size={14} />
            {busy ? "Scarico…" : `Scarica ${lines.length} prodotti · −${totalUnits}`}
          </Button>
        </>
      }
    >
      {error && (
        <Banner tone="danger" icon="x" style={{ marginBottom: 12 }}>
          {error}
        </Banner>
      )}

      <Field label="Nome batch (obbligatorio)" hint="es. Mystery Rubino 25/07">
        <input
          className="tcg-input"
          value={label}
          autoFocus
          placeholder="Mystery Rubino 25/07"
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>

      <div style={{ marginTop: 6, marginBottom: 8 }}>
        <ProductSearch onPick={addProduct} placeholder="Aggiungi prodotto al batch…" />
      </div>

      {lines.length === 0 ? (
        <div style={{ color: "var(--tcg-muted)", fontSize: 12, textAlign: "center", padding: "14px 0" }}>
          Nessun prodotto nel batch. Cerca e aggiungi i componenti.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {lines.map((l) => {
            const over = l.qty > l.stock;
            return (
              <div
                key={l.sku}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  border: `1px solid ${over ? "var(--tcg-danger)" : "var(--tcg-border)"}`,
                  borderRadius: 12,
                  padding: "8px 10px",
                  background: "var(--tcg-card)",
                }}
              >
                <ProductThumb imageUrl={l.image_url} sku={l.sku} category={l.category} size="xs" radius={9} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {l.name}
                  </div>
                  <div className="tcg-mono" style={{ fontSize: 9.5, color: over ? "var(--tcg-danger)" : "var(--tcg-muted)", marginTop: 1 }}>
                    {l.sku} · stock {l.stock}
                  </div>
                </div>
                <QtyStepper value={l.qty} onChange={(v) => setQty(l.sku, v)} min={1} max={Math.max(1, l.stock)} />
                <button
                  type="button"
                  onClick={() => removeLine(l.sku)}
                  aria-label="rimuovi"
                  style={{ border: "none", background: "transparent", color: "var(--tcg-danger)", cursor: "pointer", display: "flex", padding: 4 }}
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: "var(--tcg-muted)", fontWeight: 700, textAlign: "right", marginTop: 2 }}>
            {lines.length} prodotti · {totalUnits} unità totali
          </div>
        </div>
      )}
    </Modal>
  );
}
